const DAY_NAMES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

// "09:00:00" / "09:00" -> minutes since midnight. Returns null for anything else.
const toMinutes = (time) => {
  if (typeof time !== "string") return null;
  const [hours, minutes] = time.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

const formatTime = (time) => {
  const total = toMinutes(time);
  if (total === null) return "";

  const hours = Math.floor(total / 60);
  const minutes = String(total % 60).padStart(2, "0");
  const suffix = hours < 12 ? "a. m." : "p. m.";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${minutes} ${suffix}`;
};

const formatRange = (open, close) => {
  const from = formatTime(open);
  const to = formatTime(close);
  return from && to ? `${from} - ${to}` : "";
};

// API1 sends PascalCase, API2 camelCase; both use DayId with the same numbering as Date.getDay().
const normalizeEntry = (entry) => {
  if (!entry) return null;

  const dayId = Number(firstDefined(entry.DayId, entry.dayId, entry.diaId));
  const open = firstDefined(entry.OpenHour, entry.openHour, entry.horaApertura);
  const close = firstDefined(entry.CloseHour, entry.closeHour, entry.horaCierre);
  const active = firstDefined(entry.Active, entry.active, entry.activo);

  if (!Number.isInteger(dayId) || dayId < 0 || dayId > 6) return null;

  return {
    dayId,
    label: DAY_NAMES[dayId],
    open,
    close,
    // Older responses have no flag: a day that is present was an open day.
    isClosed: active === false || toMinutes(open) === null || toMinutes(close) === null,
  };
};

const getEntries = (point) => {
  const raw = firstDefined(
    point?.OpeningHours, point?.openingHours, point?.horarioApertura, point?.horarioPuntoScharff
  );
  if (!Array.isArray(raw)) return [];

  return raw
    .map(normalizeEntry)
    .filter(Boolean)
    .sort((first, second) => first.dayId - second.dayId);
};

const getBreak = (point) => {
  const start = firstDefined(point?.BreakStartTime, point?.breakStartTime, point?.horaRefrigerioInicio);
  const end = firstDefined(point?.BreakEndTime, point?.breakEndTime, point?.horaRefrigerioFin);
  const range = formatRange(start, end);
  return range ? { label: "Refrigerio", hours: range } : null;
};

const hasOpeningHours = (point) => getEntries(point).length > 0;

/**
 * Week schedule plus whether the point is open right now, ready to render.
 * `now` is injectable so the result can be tested without freezing the clock.
 */
const getOpeningHours = (point, now = new Date()) => {
  const entries = getEntries(point);
  if (entries.length === 0) {
    return { hasSchedule: false, isOpenNow: false, statusLabel: "", breakTime: null, days: [] };
  }

  const todayId = now.getDay();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const today = entries.find((entry) => entry.dayId === todayId);

  const isOpenNow = !!today && !today.isClosed &&
    currentMinutes >= toMinutes(today.open) && currentMinutes <= toMinutes(today.close);

  return {
    hasSchedule: true,
    isOpenNow,
    statusLabel: isOpenNow ? "Abierto ahora" : "Cerrado ahora",
    breakTime: getBreak(point),
    days: entries.map((entry) => ({
      dayId: entry.dayId,
      label: entry.label,
      hours: entry.isClosed ? "Cerrado" : formatRange(entry.open, entry.close),
      isClosed: entry.isClosed,
      isToday: entry.dayId === todayId,
    })),
  };
};

export { getOpeningHours, hasOpeningHours, formatTime, formatRange };
